import {
  DEPOSIT_KIND_LABELS,
  DEPOSIT_KINDS,
  formatMinor,
  type BankTransaction,
  type DepositKind,
  PAYMENT_KIND_LABELS,
  PAYMENT_KINDS,
  type PaymentKind,
} from '@hvac/contracts';
import { Button, Card, CardBody, cn, Input } from '@hvac/ui';
import { useState } from 'react';

/**
 * Bank lines nothing has matched yet, split into the two jobs they are.
 *
 * Money out with no receipt is a missing receipt: without the paper, the tax
 * on it cannot be claimed, and chasing that paper is the job an accounting
 * firm spends the most hours on. So the list downloads, ready to send to
 * whoever holds the receipts.
 *
 * Money in is a deposit to explain: usually a customer paying, sometimes a
 * transfer, a loan or the owner putting money in.
 *
 * On a card or credit line the sign does not say which way money went, so
 * every line there is read as a purchase.
 */

export const isMoneyOut = (line: BankTransaction): boolean =>
  line.amountMinor.startsWith('-') ||
  line.accountKind === 'credit_card' ||
  line.accountKind === 'line_of_credit';

export function missingReceiptsCsv(lines: readonly BankTransaction[]): string {
  const cell = (value: string) =>
    /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
  const rows = [
    ['Date', 'Paid to (as the bank shows it)', 'Amount', 'Account'],
    ...lines.map((line) => [
      line.postedOn,
      line.description,
      formatMinor(line.amountMinor.replace(/^-/, '')),
      line.accountName,
    ]),
  ];
  return `${rows.map((row) => row.map(cell).join(',')).join('\r\n')}\r\n`;
}

function download(filename: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function UnmatchedLines({
  lines,
  onIgnore,
  onRematch,
  onExplain,
  onAsk,
  onExplainPayment,
}: {
  lines: readonly BankTransaction[];
  onIgnore: (line: BankTransaction) => void;
  onRematch: () => void;
  onExplain: (line: BankTransaction, as: DepositKind, note: string) => void;
  /** Sends the client a link asking for the receipt behind this line. */
  onAsk?: ((line: BankTransaction) => void) | undefined;
  /** Says what money out was when no record will: net pay, a remittance, a transfer, a loan payment, a draw. */
  onExplainPayment?: ((line: BankTransaction, as: PaymentKind, note: string) => void) | undefined;
}) {
  const missing = lines.filter(isMoneyOut);
  const deposits = lines.filter((line) => !isMoneyOut(line));
  if (lines.length === 0) return null;

  return (
    <>
      {missing.length === 0 ? null : (
        <Section
          title={`Missing receipts (${String(missing.length)})`}
          note="Money left the account and no receipt explains it yet. Without the receipt, the tax on it cannot be claimed."
          lines={missing}
          onIgnore={onIgnore}
          onAsk={onAsk}
          onExplainPayment={onExplainPayment}
          actions={
            <>
              <Button size="sm" variant="secondary" onClick={onRematch}>
                Look for matches again
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  download(
                    `missing-receipts-${new Date().toISOString().slice(0, 10)}.csv`,
                    missingReceiptsCsv(missing),
                  )
                }
              >
                Download the list
              </Button>
            </>
          }
        />
      )}
      {deposits.length === 0 ? null : (
        <Section
          title={`Deposits to explain (${String(deposits.length)})`}
          note="Money came in. Say what it was. A customer payment becomes income, with the HST worked out at your province's rate; check each against its invoice before signing."
          lines={deposits}
          onIgnore={onIgnore}
          onExplain={onExplain}
        />
      )}
    </>
  );
}

function Section({
  title,
  note,
  lines,
  onIgnore,
  onExplain,
  onAsk,
  onExplainPayment,
  actions,
}: {
  title: string;
  note: string;
  lines: readonly BankTransaction[];
  onIgnore: (line: BankTransaction) => void;
  onExplain?: (line: BankTransaction, as: DepositKind, note: string) => void;
  onAsk?: ((line: BankTransaction) => void) | undefined;
  onExplainPayment?: ((line: BankTransaction, as: PaymentKind, note: string) => void) | undefined;
  actions?: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
          {title}
        </h2>
        <div className="flex gap-2">{actions}</div>
      </div>
      <p className="text-xs text-content-subtle">{note}</p>
      <Card>
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <tbody>
              {lines.map((line) => (
                <tr key={line.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2 whitespace-nowrap">{line.postedOn}</td>
                  <td className="max-w-0 truncate px-3 py-2 text-content-muted">
                    {line.description}
                  </td>
                  <td
                    className={cn(
                      'tabular px-3 py-2 text-right whitespace-nowrap',
                      isMoneyOut(line) ? 'text-content' : 'text-success',
                    )}
                  >
                    {formatMinor(line.amountMinor)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {onExplain === undefined ? (
                      <span className="inline-flex items-center gap-1">
                        {onExplainPayment === undefined ? null : (
                          <PaymentExplainControl
                            onExplain={(as, note) => onExplainPayment(line, as, note)}
                          />
                        )}
                        {onAsk === undefined ? null : (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onAsk(line)}
                            title="Send the client a link asking for this receipt"
                          >
                            Ask the client
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onIgnore(line)}
                          title="Not a business transaction"
                        >
                          Ignore
                        </Button>
                      </span>
                    ) : (
                      <ExplainControl onExplain={(as, note) => onExplain(line, as, note)} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </section>
  );
}

/** "What was this?" for money out that no receipt will ever explain. */
function PaymentExplainControl({
  onExplain,
}: {
  onExplain: (as: PaymentKind, note: string) => void;
}) {
  const [choice, setChoice] = useState<PaymentKind | ''>('');
  const [note, setNote] = useState('');
  return (
    <span className="inline-flex items-center gap-1">
      <select
        aria-label="What was this payment?"
        className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 text-sm"
        value={choice}
        onChange={(event) => setChoice(event.target.value as PaymentKind | '')}
      >
        <option value="">What was this?</option>
        {PAYMENT_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {PAYMENT_KIND_LABELS[kind]}
          </option>
        ))}
      </select>
      {choice === 'other' ? (
        <Input
          aria-label="What it was"
          className="w-32"
          placeholder="What it was"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      ) : null}
      {choice === '' ? null : (
        <Button
          size="sm"
          variant="secondary"
          disabled={choice === 'other' && note.trim() === ''}
          onClick={() => onExplain(choice, note)}
        >
          Save
        </Button>
      )}
    </span>
  );
}

/** "What was this?" for one deposit. "Something else" asks for a word or two. */
function ExplainControl({ onExplain }: { onExplain: (as: DepositKind, note: string) => void }) {
  const [choice, setChoice] = useState<DepositKind | ''>('');
  const [note, setNote] = useState('');
  return (
    <div className="flex items-center justify-end gap-2">
      <select
        aria-label="What was this deposit?"
        className="rounded-[var(--radius-control)] border border-border bg-surface px-2 py-1 text-sm"
        value={choice}
        onChange={(event) => setChoice(event.target.value as DepositKind | '')}
      >
        <option value="">What was this?</option>
        {DEPOSIT_KINDS.map((kind) => (
          <option key={kind} value={kind}>
            {DEPOSIT_KIND_LABELS[kind]}
          </option>
        ))}
      </select>
      {choice === 'other' ? (
        <Input
          aria-label="What it was"
          className="w-40"
          placeholder="What it was"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      ) : null}
      <Button
        size="sm"
        variant="secondary"
        disabled={choice === '' || (choice === 'other' && note.trim() === '')}
        onClick={() => {
          if (choice !== '') onExplain(choice, note);
        }}
      >
        Save
      </Button>
    </div>
  );
}
